import { createStyleSheet, Focusable, IconButton, Loader, useStyleSheet, useThemeContext, View } from '@gopeerproject/ui-kit';
import throttle from 'lodash/throttle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { pdfjs, Document, Page, Thumbnail } from 'react-pdf';
import { Header } from './';
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
export const PDF = ({ file, onClose }) => {
    const styles = useStyleSheet(getStyles);
    const { size } = useThemeContext();
    const PDF = useRef();
    const navigation = useRef(null);
    const viewer = useRef(null);
    const doc = useRef(null);
    const [page, setPage] = useState(1);
    const [numPages, setNumPages] = useState(0);
    const pages = useMemo(() => [...Array(numPages).keys()], [numPages]);
    const [isLoading, setIsLoading] = useState(true);
    const [scale, setScale] = useState(1);
    useEffect(() => {
        if (navigation.current instanceof HTMLElement && numPages)
            navigation.current.scrollTo({
                top: navigation.current.children[page - 1].offsetTop -
                    size.K,
                behavior: 'smooth'
            });
    }, [page, numPages, size.K]);
    const navigate = (i) => {
        var _a, _b;
        if (!doc.current || !(viewer.current instanceof HTMLElement))
            return;
        const { pages } = doc.current;
        viewer.current.scrollTo({
            top: ((_b = (_a = pages[i]) === null || _a === void 0 ? void 0 : _a.offsetTop) !== null && _b !== void 0 ? _b : 0) - size.K,
            behavior: 'smooth'
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Header, { isFixed: true, file: file, onClose: onClose, actions: React.createElement(React.Fragment, null,
                React.createElement(IconButton, { type: 'tertiary', size: 'sm', accent: 'neutral', value: 'zoom-out', onPress: () => {
                        setScale(Math.max(0.25, scale - 0.25));
                    } }),
                React.createElement(IconButton, { type: 'tertiary', size: 'sm', accent: 'neutral', value: 'zoom-in', onPress: () => {
                        setScale(Math.min(5, scale + 0.25));
                    } })) }),
        React.createElement(View, { style: styles.container },
            React.createElement(Document, { file: file.data, ref: doc, onLoadSuccess: (pdf) => {
                    PDF.current = pdf;
                    setNumPages(pdf.numPages);
                    setIsLoading(false);
                }, loading: () => React.createElement(Loader, { full: true }) },
                React.createElement(View, { style: styles.wrapper },
                    React.createElement(View, { style: styles.navigation, ref: navigation }, !isLoading &&
                        pages.map((i) => (React.createElement(Focusable, { key: i, style: [
                                styles.navigationPage,
                                page === i + 1 && styles.navigationPageSelected
                            ], hoverStyle: styles.navigationPageHovered, onPress: () => {
                                navigate(i);
                            } },
                            React.createElement(Thumbnail, { pageNumber: i + 1, width: 180 }))))),
                    React.createElement(View, { ref: viewer, style: styles.viewer, 
                        //@ts-expect-error onScroll does exist on View and works fine
                        onScroll: throttle((e) => {
                            if (!doc.current)
                                return;
                            // weird but pages is nested inside doc.current.pages.current now for some reason, used to be just doc.current.pages
                            const pages = doc.current.pages.current;
                            setPage(pages.length -
                                [...pages]
                                    .reverse()
                                    .findIndex((v) => e.target.scrollTop + 200 >
                                    v.offsetTop));
                        }, 500) }, pages.map((i) => (React.createElement(View, { key: i, style: styles.viewerPage },
                        React.createElement(Page, { pageNumber: i + 1, scale: scale }))))))))));
};
const getStyles = createStyleSheet(({ size, color }) => ({
    container: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        width: '100%',
        position: 'relative'
    },
    wrapper: {
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        flexDirection: 'row',
        overflow: 'hidden'
    },
    navigation: {
        padding: size.K,
        // @ts-expect-error auto works best here
        overflow: 'auto',
        maxHeight: '100%',
        gap: size.G,
        borderColor: color.white.p10,
        borderRightWidth: 1,
        borderStyle: 'solid'
    },
    navigationPage: {
        padding: size.B,
        borderRadius: size.C,
        borderWidth: size.B,
        borderStyle: 'solid',
        borderColor: color.black.p100,
        transitionProperty: 'border-color'
    },
    navigationPageSelected: { borderColor: color.primary.solid },
    navigationPageHovered: { borderColor: color.primary.solidHover },
    viewer: {
        // @ts-expect-error auto works best here
        overflow: 'auto',
        maxHeight: '100%',
        flexGrow: 1,
        flexShrink: 1,
        alignItems: 'center',
        padding: size.K,
        paddingBottom: 0
    },
    // @ts-expect-error again this is web so position static will work
    viewerPage: { marginBottom: size.K, position: 'static' }
}));
//# sourceMappingURL=PDF.js.map